import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormField, Input } from '../../components/ui/FormField.js';
import { api } from '../../services/api.js';

/** 364 (#1217): one person the platform may make a new supplier's first Host (`GET /suppliers/staff-candidates`). */
export interface StaffCandidate {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  is_customer: boolean;
  memberships: Array<{ supplier_id: string; supplier_name: string; role: string }>;
}

export function candidateName(c: StaffCandidate) {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email;
}

/**
 * 364 (design 1a): search active, verified users by name or email (2 characters or more) and pick one. Administrators
 * are never offered (the platform filters them). The discount page's CustomerPicker pattern: debounced, a short list.
 */
export function StaffCandidatePicker({
  error,
  onPick,
}: {
  error?: string;
  onPick: (candidate: StaffCandidate) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 400);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery({
    queryKey: ['supplier-staff-candidates', debounced],
    queryFn: () =>
      api.get<{ items: StaffCandidate[] }>(
        `/suppliers/staff-candidates?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length >= 2,
  });
  const results = debounced.length >= 2 ? (data?.items ?? []) : [];

  return (
    <FormField
      label="Find the user"
      name="staff_search"
      required
      error={error}
      hint="Search by name or email (2 letters or more)."
    >
      <Input
        id="staff_search"
        placeholder="Search by name or email…"
        value={term}
        onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
      />
      {results.length > 0 && (
        <ul className="mt-1 max-h-60 divide-y divide-hairline overflow-y-auto rounded-md border border-hairline bg-canvas text-sm">
          {results.map((c) => (
            <li key={c._id}>
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-ink hover:bg-surface"
                onClick={() => onPick(c)}
              >
                <span>{candidateName(c)}</span>
                <span className="text-xs text-steel">{c.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {debounced.length >= 2 && !isFetching && data && results.length === 0 && (
        <p className="mt-1 text-sm text-steel">No active user with a verified email matches.</p>
      )}
    </FormField>
  );
}
